import { useCallback, useEffect, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { styled } from '@mui/material/styles';
import { CreateAiProviderKeyModal, QButton, QCard, QCardContent, QIconButton, QTypography } from '@kw/quokka-ui';
import { useSnackbar } from 'notistack';
import { KwConfirmationModal } from '../../kw-ui-components/KwConfirmationModal';
import { createAiProviderKey, deleteAiProviderKey, listAiProviderKeys } from '../../services/aiApi.service';
import { AiProviderKey, CreateAiProviderKeyPayload } from '../../types/ai';

interface AiProviderKeysCardProps {
  accessToken: string;
}

const keyId = (key: AiProviderKey) => key.id || key.keyId;
const maskedKey = (key: AiProviderKey) => `${key.tokenPrefix ?? key.prefix ?? ''}••••${key.tokenLast4 ?? key.last4 ?? ''}`;

export function AiProviderKeysCard({ accessToken }: AiProviderKeysCardProps) {
  const [keys, setKeys] = useState<AiProviderKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<AiProviderKey | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const refreshKeys = useCallback(async () => {
    try {
      setKeys(await listAiProviderKeys(accessToken));
    } catch {
      enqueueSnackbar('Failed to load AI provider keys', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [accessToken, enqueueSnackbar]);

  useEffect(() => {
    refreshKeys();
  }, [refreshKeys]);

  const createKey = (payload: CreateAiProviderKeyPayload) => createAiProviderKey(accessToken, payload);

  const deleteKey = async () => {
    if (!keyToDelete) return;
    try {
      await deleteAiProviderKey(accessToken, keyId(keyToDelete));
      setKeys(current => current.filter(key => keyId(key) !== keyId(keyToDelete)));
      enqueueSnackbar('AI provider key deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to delete AI provider key', { variant: 'error' });
    }
  };

  return (
    <QCard>
      <QCardContent>
        <CardHeader>
          <TitleRow>
            <QTypography variant="h4Bold">AI Provider Keys</QTypography>
            {keys.length > 0 && (
              <AddKeyButton variant="filled" onClick={() => setIsCreateOpen(true)}>
                Add key
              </AddKeyButton>
            )}
          </TitleRow>
          <QTypography variant="bodyRegular" sx={{ display: 'block', marginTop: 1 }}>
            Add your own provider key to generate AI reports. Keys are encrypted at rest.
          </QTypography>
        </CardHeader>

        {!loading && keys.length === 0 && (
          <EmptyState>
            <QTypography variant="bodyRegular">No AI provider keys have been added.</QTypography>
            <QButton variant="filled" onClick={() => setIsCreateOpen(true)}>
              Add AI provider key
            </QButton>
          </EmptyState>
        )}

        {keys.map(key => (
          <KeyRow key={keyId(key)}>
            <div>
              <QTypography variant="h5Bold">{key.keyName || key.model}</QTypography>
              <QTypography variant="bodyRegular" sx={{ display: 'block' }}>
                {key.model}
              </QTypography>
            </div>
            <MaskedKey>{maskedKey(key)}</MaskedKey>
            <QIconButton aria-label={`Delete ${key.keyName || key.model}`} onClick={() => setKeyToDelete(key)}>
              <DeleteOutlineIcon />
            </QIconButton>
          </KeyRow>
        ))}
      </QCardContent>

      <CreateAiProviderKeyModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        createAiApiKeyCallback={createKey}
        onCreated={() => {
          refreshKeys();
          enqueueSnackbar('AI provider key added', { variant: 'success' });
        }}
      />

      <KwConfirmationModal
        title="Delete AI provider key"
        isOpen={Boolean(keyToDelete)}
        onClose={() => setKeyToDelete(null)}
        description={`delete "${keyToDelete?.keyName || keyToDelete?.model}"`}
        onConfirm={deleteKey}
      />
    </QCard>
  );
}

const CardHeader = styled('div')`
  margin-bottom: 20px;
`;

const TitleRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const AddKeyButton = styled(QButton)`
  flex-shrink: 0;
  min-width: max-content;
  white-space: nowrap;
`;

const EmptyState = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 0 8px;
`;

const KeyRow = styled('div')`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(150px, auto) auto;
  align-items: center;
  gap: 20px;
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.palette.grey[300]};
`;

const MaskedKey = styled('code')`
  color: ${({ theme }) => theme.palette.grey[700]};
`;
